import { useState, useEffect, useRef } from "react";
import { api } from "@/App";
import { 
  Car, Upload, Search, Plus, Trash2, Edit, 
  Fuel, Gauge, Calendar, User, FileJson, Link2, LinkIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ENERGIES = [
  { value: "ES", label: "Essence" },
  { value: "GO", label: "Diesel" },
  { value: "EL", label: "Électrique" },
  { value: "HY", label: "Hybride" },
  { value: "GP", label: "GPL" },
];

export default function VehiculesDBView() {
  const [vehicules, setVehicules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editVehicule, setEditVehicule] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const limit = 50;
  
  const [newVehicule, setNewVehicule] = useState({
    immatriculation: "",
    marque: "",
    modele: "",
    energie: "",
    km: "",
    couleur: "",
    client_nom: ""
  });

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [page, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/vehicules-db?search=${searchTerm}&limit=${limit}&skip=${page * limit}`);
      setVehicules(response.data.vehicules);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await api.get("/vehicules-db/stats");
      setStats(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleAddVehicule = async () => {
    if (!newVehicule.immatriculation && !newVehicule.marque) {
      toast.error("Immatriculation ou marque obligatoire");
      return;
    }
    setSaving(true);
    try {
      if (editVehicule) {
        await api.put(`/vehicules-db/${editVehicule.id}`, newVehicule);
        toast.success("Véhicule modifié");
      } else {
        await api.post("/vehicules-db", newVehicule);
        toast.success("Véhicule ajouté");
      }
      setDialogOpen(false);
      setEditVehicule(null);
      setNewVehicule({ immatriculation: "", marque: "", modele: "", energie: "", km: "", couleur: "", client_nom: "" });
      fetchData();
      fetchStats();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const handleEditClick = (vehicule) => {
    setEditVehicule(vehicule);
    setNewVehicule({
      immatriculation: vehicule.immatriculation || "",
      marque: vehicule.marque || "",
      modele: vehicule.modele || "",
      energie: vehicule.energie || "",
      km: vehicule.km || "",
      couleur: vehicule.couleur || "",
      client_nom: vehicule.client_nom || ""
    });
    setDialogOpen(true);
  };

  const handleDeleteVehicule = async (vehiculeId) => {
    if (!confirm("Supprimer ce véhicule ?")) return;
    try {
      await api.delete(`/vehicules-db/${vehiculeId}`);
      toast.success("Véhicule supprimé");
      fetchData();
      fetchStats();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await api.post("/vehicules-db/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${response.data.imported} véhicules importés, ${response.data.linked} reliés à des clients`);
      setImportDialog(false);
      fetchData();
      fetchStats();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message).join(", ") : (typeof detail === 'object' ? JSON.stringify(detail) : detail);
      toast.error(msg || "Erreur lors de l'import");
    }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getEnergieLabel = (code) => {
    const e = ENERGIES.find(en => en.value === code);
    return e?.label || code || "-";
  };

  const getEnergieBadge = (code) => {
    const colors = {
      ES: "bg-amber-100 text-amber-700",
      GO: "bg-slate-100 text-slate-700",
      EL: "bg-emerald-100 text-emerald-700",
      HY: "bg-blue-100 text-blue-700",
      GP: "bg-purple-100 text-purple-700",
    };
    return colors[code] || "bg-slate-100 text-slate-700";
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="vehicules-db-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Car className="h-8 w-8" />
            Base Véhicules
          </h1>
          <p className="text-slate-600 mt-1">{total} véhicule(s) enregistré(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Importer
          </Button>
          <Button onClick={() => { setEditVehicule(null); setNewVehicule({ immatriculation: "", marque: "", modele: "", energie: "", km: "", couleur: "", client_nom: "" }); setDialogOpen(true); }} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Car className="h-8 w-8 text-slate-600" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-500">Total véhicules</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="p-4 flex items-center gap-3">
              <LinkIcon className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-600">{stats.with_client}</p>
                <p className="text-sm text-slate-500">Reliés à un client</p>
              </div>
            </CardContent>
          </Card>
          {stats.top_marques?.slice(0, 2).map((m, idx) => (
            <Card key={idx} className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-lg font-bold">{m.marque}</p>
                <p className="text-sm text-slate-500">{m.count} véhicules</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher (immatriculation, marque, modèle, client...)"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : vehicules.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Car className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun véhicule trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Immatriculation</TableHead>
                  <TableHead>Marque / Modèle</TableHead>
                  <TableHead>Énergie</TableHead>
                  <TableHead>Km</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicules.map((v) => (
                  <TableRow key={v.id} className="hover:bg-slate-50">
                    <TableCell>
                      <span className="font-mono font-bold text-slate-900">{v.immatriculation || "-"}</span>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold">{v.marque}</p>
                      <p className="text-sm text-slate-500">{v.modele}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={getEnergieBadge(v.energie)}>
                        {getEnergieLabel(v.energie)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {v.km ? `${parseInt(v.km).toLocaleString()} km` : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {v.client_id ? (
                          <LinkIcon className="h-4 w-4 text-emerald-500" title="Relié à un client" />
                        ) : (
                          <User className="h-4 w-4 text-slate-300" />
                        )}
                        <span className={v.client_id ? "text-emerald-700" : "text-slate-600"}>
                          {v.client_nom || "-"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEditClick(v)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteVehicule(v.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Page {page + 1} sur {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editVehicule ? "Modifier le véhicule" : "Nouveau véhicule"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Immatriculation</Label>
                <Input
                  value={newVehicule.immatriculation}
                  onChange={(e) => setNewVehicule({ ...newVehicule, immatriculation: e.target.value.toUpperCase() })}
                  placeholder="AB-123-CD"
                />
              </div>
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input
                  value={newVehicule.marque}
                  onChange={(e) => setNewVehicule({ ...newVehicule, marque: e.target.value.toUpperCase() })}
                  placeholder="RENAULT"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Modèle</Label>
              <Input
                value={newVehicule.modele}
                onChange={(e) => setNewVehicule({ ...newVehicule, modele: e.target.value })}
                placeholder="CLIO IV"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Énergie</Label>
                <Select
                  value={newVehicule.energie}
                  onValueChange={(v) => setNewVehicule({ ...newVehicule, energie: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENERGIES.map((e) => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kilométrage</Label>
                <Input
                  value={newVehicule.km}
                  onChange={(e) => setNewVehicule({ ...newVehicule, km: e.target.value.replace(/\D/g, '') })}
                  placeholder="50000"
                />
              </div>
              <div className="space-y-2">
                <Label>Couleur</Label>
                <Input
                  value={newVehicule.couleur}
                  onChange={(e) => setNewVehicule({ ...newVehicule, couleur: e.target.value })}
                  placeholder="Blanc"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nom du client</Label>
              <Input
                value={newVehicule.client_nom}
                onChange={(e) => setNewVehicule({ ...newVehicule, client_nom: e.target.value })}
                placeholder="DUPONT JEAN"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddVehicule} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : (editVehicule ? "Modifier" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5" />
              Importer des véhicules
            </DialogTitle>
            <DialogDescription>
              Importez des véhicules depuis un fichier JSON
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <Car className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <p className="text-sm text-slate-600 mb-4">
                Les véhicules seront automatiquement reliés aux clients existants
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportFile}
                className="hidden"
                id="vehicule-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? (
                  <>
                    <div className="spinner mr-2" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Choisir un fichier JSON
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
